import React from "react";
import { ArrowRight, AlertTriangle, Loader2 } from "lucide-react";
import { Dialog, PrimaryButton, SecondaryButton, fmtDate, plural } from "./ui";

// Spells out, per lab, what the assignment change does: labs switch source
// instantly; unbooked cart items from the old source are dropped at checkout
// (their test ids aren't in the lab's new list); confirmed bookings are untouched.
const Row = ({ name, children, warn = false }) => (
  <li className="flex items-start gap-2 py-1.5">
    <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${warn ? "bg-amber-500" : "bg-[#00A6A6]"}`} />
    <span className="min-w-0 text-[13px] text-gray-700">
      <span className="font-medium text-gray-900">{name}</span> {children}
    </span>
  </li>
);

const Group = ({ title, children }) => (
  <div className="mt-3">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{title}</p>
    <ul className="mt-1 max-h-40 overflow-y-auto pr-1">{children}</ul>
  </div>
);

const ConfirmChangesDialog = ({ open, list, diff, busy, onCancel, onConfirm }) => {
  if (!list || !diff) return null;
  const listTests = plural(list.test_count, "test");
  const fresh = diff.added.filter((lab) => !diff.moved.includes(lab));
  const zeroFallback = diff.removed.filter((lab) => !lab.direct_test_count).length;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      busy={busy}
      icon={zeroFallback ? AlertTriangle : ArrowRight}
      tone={zeroFallback ? "amber" : "teal"}
      title={`Update labs using “${list.name}”?`}
      actions={(
        <>
          <SecondaryButton onClick={onCancel} disabled={busy}>Cancel</SecondaryButton>
          <PrimaryButton onClick={onConfirm} disabled={busy}>
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Saving…" : "Confirm changes"}
          </PrimaryButton>
        </>
      )}
    >
      {fresh.length > 0 && (
        <Group title={`Start using this list · ${fresh.length}`}>
          {fresh.map((lab) => (
            <Row key={lab.id} name={lab.brand_name}>
              switches from its direct upload ({plural(lab.direct_test_count, "test")}) to {list.name} ({listTests}).
            </Row>
          ))}
        </Group>
      )}
      {diff.moved.length > 0 && (
        <Group title={`Move from another list · ${diff.moved.length}`}>
          {diff.moved.map((lab) => (
            <Row key={lab.id} name={lab.brand_name}>
              moves from {lab.test_list?.name} ({plural(lab.test_count, "test")}) to {list.name} ({listTests}).
            </Row>
          ))}
        </Group>
      )}
      {diff.removed.length > 0 && (
        <Group title={`Stop using this list · ${diff.removed.length}`}>
          {diff.removed.map((lab) =>
            lab.direct_test_count ? (
              <Row key={lab.id} name={lab.brand_name}>
                reverts to its own direct test list ({plural(lab.direct_test_count, "test")}
                {lab.direct_uploaded_at ? `, uploaded ${fmtDate(lab.direct_uploaded_at)}` : ""}).
              </Row>
            ) : (
              <Row key={lab.id} name={lab.brand_name} warn>
                has no direct test list — it will show <span className="font-medium">0 tests</span> and can’t be
                booked until one is uploaded from the lab page.
              </Row>
            )
          )}
        </Group>
      )}
      <p className="mt-4 text-xs text-gray-500">
        Changes apply immediately. Tests already in customers’ carts for these labs are removed at checkout and must
        be added again from the new list. Confirmed bookings are not affected.
      </p>
    </Dialog>
  );
};

export default ConfirmChangesDialog;
